import { fabric } from 'fabric';
import { Observable, fromEvent } from 'rxjs';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { MAX_FIT_TO_VIEW_CONTENT_HEIGHT, MAX_ZOOM } from 'src/app/services/sessions-vpt.service';
import { ElementRef, ViewContainerRef } from '@angular/core';
import { clamp } from 'lodash';
import { ItemModel } from 'src/app/models/itemModel';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { isBoardItem, isBoardLine } from '../../common/y-session';
import { FabricObject } from '../items-canvas/board-items/board-items-manager';
import { BoardItemsStateManager } from '../items-canvas/board-items/board-items-state-manager';
import { customFabricFields } from '../../../common/interfaces/sync-service-interface';
import {
  getScale,
  handleHorizontalGroupScalingForTextbox,
  handleUniformScalingForTextbox,
  shouldDisableScalingDueToFontSizeLimit,
} from './textbox/textbox-scaling';
import { cleanAllStyles, updateMixedFontSizesState } from './textbox/textbox-styles';
interface fabricInitializeOptions {
  objectLockFeatureEnabled?: boolean;
  telemetryService?: TelemetryService;
}

export interface ObjectAbsoluteCoords {
  tl_x: number;
  tl_y: number;
  br_x: number;
  br_y: number;
}

export const SCALE_LIMITS = {
  MIN: 1e-3,
  MAX: 1e3,
};

export function initialize(mobileView: boolean, initOptions: fabricInitializeOptions = {}): void {
  // ---- Fabric JS Performance Optimizations -----
  (fabric.Canvas.prototype as any)._renderObjects = function (
    ctx: CanvasRenderingContext2D,
    objects: fabric.Object[],
  ) {
    for (let i = 0; i < objects.length; i++) {
      objects[i]?.render(ctx);
    }
  };

  (fabric.Canvas.prototype as any).moveTo = function (object, index) {
    const idx = this._objects.indexOf(object);
    if (idx !== -1) {
      this._objects.splice(idx, 1);
    }
    this._objects.splice(index, 0, object);

    // In case we want to execute any logic once the stacking order changes
    // Because there is no function in the object itself other than bring to front and send to back that are responsible for the changes in stacking
    object.onMoveToIndex?.(index);

    return this.renderOnAddRemove && this.requestRenderAll();
  };

  // Optimization: Remove the call to save / restore before and after these functions run
  //               as the loop that runs render will call that before any particular object is rendered
  fabric.Object.prototype._renderFill = function (ctx: CanvasRenderingContext2D) {
    if (!this.fill) {
      return;
    }
    (this as any)._setFillStyles(ctx, this);
    if (this.fillRule === 'evenodd') {
      ctx.fill('evenodd');
    } else {
      ctx.fill();
    }
  };

  fabric.Object.prototype._renderStroke = function (ctx: CanvasRenderingContext2D) {
    if (!this.stroke || this.strokeWidth === 0) {
      return;
    }
    if (this.shadow && !(this.shadow as any).affectStroke) {
      this._removeShadow(ctx);
    }
    if (this.strokeUniform && this.group) {
      const scaling = this.getObjectScaling();
      ctx.scale(1 / scaling.scaleX, 1 / scaling.scaleY);
    } else if (this.strokeUniform) {
      ctx.scale(1 / this.scaleX!, 1 / this.scaleY!);
    }
    this._setLineDash(ctx, this.strokeDashArray!);
    (this as any)._setStrokeStyles(ctx, this);
    ctx.stroke();
  };

  // Optimization: If the two objects being checked for intersection are rectangles (common-case) then we can highly optimize the
  //               intersection calculation by doing a simple rectangle intersection check
  (fabric.Object.prototype.intersectsWithRect = function (
    pointTL: fabric.Point,
    pointBR: fabric.Point,
    absolute: boolean,
    calculate: boolean,
  ) {
    const coords = this.getCoords(absolute, calculate);
    if (isRectangle(coords[0], coords[1], coords[2], coords[3])) {
      return doRectanglesIntersectOnly(pointTL, pointBR, coords[0], coords[2]);
    }

    const intersection = fabric.Intersection.intersectPolygonRectangle(coords, pointTL, pointBR);
    return intersection.status === 'Intersection';
  }),
    // Optimization: Remove the call to setCoords for all objects because the sessions-vpt service will do it once the VPT value becomes stable
    (fabric.StaticCanvas.prototype.setViewportTransform = function (vpt: number[]) {
      const activeObject = this._activeObject;
      const backgroundObject = this.backgroundImage as any;
      const overlayObject = this.overlayImage;

      this.viewportTransform = vpt;
      if (activeObject) {
        activeObject.setCoords();
      }
      if (backgroundObject) {
        backgroundObject.setCoords(true);
      }
      if (overlayObject) {
        overlayObject.setCoords(true);
      }
      this.calcViewportBoundaries();
      this.renderOnAddRemove && this.requestRenderAll();
      return this as any;
    });

  // Optimization: Remove the caching logic for calcTransformMatrix as its slower than doing the math
  (fabric.Object.prototype.calcTransformMatrix = function (skipGroup: boolean) {
    let matrix = this.calcOwnMatrix();
    if (skipGroup || !this.group) {
      return matrix;
    }
    if (this.group) {
      matrix = fabric.util.multiplyTransformMatrices(this.group.calcTransformMatrix(false), matrix);
    }

    return matrix;
  }),
    (fabric.Object.prototype.calcOwnMatrix = function (this, ...args) {
      const calcTranslateMatrix = (fabric.Object.prototype as any)
        ._calcTranslateMatrix as () => number[];
      const tMatrix = calcTranslateMatrix.bind(this)();
      const options = {
        angle: this.angle,
        translateX: tMatrix[4],
        translateY: tMatrix[5],
        scaleX: this.scaleX,
        scaleY: this.scaleY,
        skewX: this.skewX,
        skewY: this.skewY,
        flipX: this.flipX,
        flipY: this.flipY,
      };
      return fabric.util.composeMatrix(options as any);
    });

  // don't include default values to reduce the size of serialized Fabric object
  fabric.Object.prototype.includeDefaultValues = false;
  fabric.Object.prototype.hasBorders = true;
  fabric.Object.prototype.cornerColor = '#bbd8ff';
  fabric.Object.prototype.borderColor = '#0082f5';
  fabric.Object.prototype.cornerStrokeColor = '#0082f5';
  fabric.Object.prototype.transparentCorners = false;
  fabric.Object.prototype.cornerSize = 8;
  fabric.Object.prototype.cornerStyle = 'circle';
  fabric.Object.prototype.perPixelTargetFind = true;
  // disable the rotation.
  fabric.Object.prototype.rotatingPointOffset = 4;
  // Ensure sharp edges while scaling cached objects by regenerating cache during transforms
  fabric.Object.prototype.noScaleCache = false;
  fabric.IText.prototype.setControlsVisibility({
    tr: false,
    bl: false,
    ml: false,
    mt: false,
    mr: false,
    mb: false,
    mtr: false,
    tl: false,
    br: false,
  });
  fabric.Line.prototype.setControlsVisibility({
    tr: true,
    bl: true,
    ml: false,
    mt: false,
    mr: false,
    mb: false,
    mtr: false,
    tl: false,
    br: false,
  });
  fabric.Line.prototype.borderColor = 'transparent';

  fabric.Object.prototype.onSelect = function (options): boolean {
    if (
      (this.canvas as YCanvas)?.isCanvasLockedForUser &&
      !(this.canvas as YCanvas)?.canvasItemsInteractionsEnabled
    ) {
      return true;
    }
    if (!initOptions.objectLockFeatureEnabled) {
      return false;
    }

    const isLocked = Boolean((this as any).get('locked'));

    toggleLockObject(this, isLocked);

    return false;
  };

  fabric.Line.prototype.drawBorders = function (ctx: CanvasRenderingContext2D): any {
    const { flipX, flipY, y1, y2, x1, x2 } = this;
    const zoom = this.canvas!.getZoom();

    const isLeftToRight = x1! <= x2!;
    const isBottomToTop = y1! >= y2!;
    const directionCondition =
      (isLeftToRight === isBottomToTop && flipX === flipY) ||
      (isLeftToRight !== isBottomToTop && flipX !== flipY);
    const origin = this.getCenterPoint();
    const pointA = directionCondition
      ? this.getPointByOrigin('left', 'bottom')
      : this.getPointByOrigin('left', 'top');
    const pointB = directionCondition
      ? this.getPointByOrigin('right', 'top')
      : this.getPointByOrigin('right', 'bottom');

    ctx.strokeStyle = '#0082f5';
    ctx.beginPath();
    ctx.moveTo((origin.x - pointA.x) * zoom, (origin.y - pointA.y) * zoom);
    ctx.lineTo((origin.x - pointB.x) * zoom, (origin.y - pointB.y) * zoom);
    ctx.closePath();
    ctx.stroke();

    this.setControlsVisibility({
      tr: directionCondition,
      tl: !directionCondition,
      bl: directionCondition,
      br: !directionCondition,
    });
  };

  const toggleLockObject = function (object: fabric.Object, isLocked: boolean): void {
    // The logic of this function should be deprecated and moved to the board item component
    if (isBoardItem(object)) {
      return;
    }
    const isTextBox = object.type === 'textbox';
    // preserve the state of the object in unlocked state in 'on_unlocked_state' attribute
    // so when we unlock the object later
    let onUnlockedState = (object as any).get('on_unlocked_state');
    if (!onUnlockedState) {
      onUnlockedState = {
        lockMovementX: object.lockMovementX,
        lockMovementY: object.lockMovementY,
        lockRotation: object.lockRotation,
        lockScalingFlip: object.lockScalingFlip,
        lockScalingX: object.lockScalingX,
        lockScalingY: object.lockScalingY,
        lockSkewingX: object.lockSkewingX,
        lockSkewingY: object.lockSkewingY,
        lockUniScaling: object.lockUniScaling,
        controls: object.controls,
      };

      if (isTextBox) {
        onUnlockedState.editable = (object as fabric.Textbox).editable;
      }
      (object as any).set('on_unlocked_state', onUnlockedState);
    }

    if (isLocked) {
      object.lockMovementX = true;
      object.lockMovementY = true;
      object.lockRotation = true;
      object.lockScalingFlip = true;
      object.lockScalingX = true;
      object.lockScalingY = true;
      object.lockSkewingX = true;
      object.lockSkewingY = true;
      object.lockUniScaling = true;
      object.controls = {};
      object.hoverCursor = 'pointer';
    } else {
      // unlocking the object
      object.set(onUnlockedState);
      object.hoverCursor = undefined;
    }

    // prevent editing if it's a textbox
    if (isTextBox) {
      (object as fabric.Textbox).editable = !isLocked;
    }
  };

  // enable only corner controls
  fabric.Line.prototype.controls = Object.keys(fabric.Line.prototype.controls)
    .filter((key) => ['tr', 'tl', 'br', 'bl'].includes(key))
    .map((controlKey) => {
      const control = fabric.Line.prototype.controls[controlKey];
      // change control style
      control.cursorStyleHandler = (
        event: MouseEvent,
        ctrl: fabric.Control,
        object: fabric.Object,
      ): string => {
        if (object.type === 'line') {
          return 'crosshair';
        }
        return ['tl', 'br'].includes(controlKey) ? 'nwse-resize' : 'nesw-resize';
      };

      return { [controlKey]: control };
    })
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

  initializeRotationControllers();
  initializeScalingControllers();
  overridePolygoneInitialization();
  overrideTextboxInitialization();
}

function initializeRotationControllers(): void {
  const rotateImg = document.createElement('img');
  rotateImg.src = '../../assets/icons/default_rotation_icon.svg';

  function renderIcon(icon) {
    return function renderIconImg(this, ctx, left, top, styleOverride, fabricObject) {
      const size = this.sizeX;
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
      ctx.drawImage(icon, -size / 2, -size / 2, size, size);
      ctx.restore();
    };
  }

  function createRotationControl(): fabric.Control {
    return new fabric.Control({
      x: 0.5,
      y: -0.5,
      offsetX: 30,
      offsetY: 20,
      actionHandler: fabric.Object.prototype.controls.mtr.actionHandler,
      cursorStyle: 'url(../../../assets/icons/main-cursor.svg), default',
      withConnection: false,
      actionName: 'rotate',
      sizeX: 20,
      sizeY: 20,
      render: renderIcon(rotateImg),
    });
  }

  // disable middle top rotation control.
  fabric.Object.prototype.controls.mtr.setVisibility(false);

  fabric.Object.prototype.controls.rotTR1 = createRotationControl();
  fabric.IText.prototype.controls.rotTR1 = createRotationControl();
  fabric.Textbox.prototype.controls.rotTR1 = createRotationControl();
}

function initializeScalingControllers(): void {
  function renderRectControl(this, ctx, left, top, styleOverride, fabricObject: fabric.Object) {
    let xSize = 8;
    let ySize = 8;
    if (this.x === 0) {
      xSize = fabricObject.getBoundingRect().width;
    } else {
      ySize = fabricObject.getBoundingRect().height;
    }
    this.sizeX = xSize;
    this.sizeY = ySize;
    ctx.save();
    ctx.fillStyle = 'transparent';
    ctx.strokeStyle = 'transparent';
    ctx.lineWidth = 1;
    ctx.translate(left, top);
    ctx.rotate(((fabricObject.angle ?? 0) * Math.PI) / 180);
    ctx.strokeRect(-xSize / 2, -ySize / 2, xSize, ySize);
    ctx.restore();

    if (fabricObject) {
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(((fabricObject.angle ?? 0) * Math.PI) / 180);
      ctx.beginPath();
      ctx.fillStyle = '#bbd8ff';
      ctx.strokeStyle = '#0082f5';
      ctx.lineWidth = 1;
      ctx.arc(-this.offsetX, 0, 4, 0, 2 * Math.PI); // Applying the offset in screen space
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
  }

  // left side
  fabric.IText.prototype.controls.ml = new fabric.Control({
    x: -0.5,
    y: 0,
    offsetX: -4,
    actionHandler: fabric.IText.prototype.controls.ml.actionHandler,
    cursorStyleHandler: fabric.IText.prototype.controls.ml.cursorStyleHandler,
    withConnection: false,
    render: renderRectControl,
  });
  // right side
  fabric.IText.prototype.controls.mr = new fabric.Control({
    x: 0.5,
    y: 0,
    offsetX: 4,
    actionHandler: fabric.IText.prototype.controls.mr.actionHandler,
    cursorStyleHandler: fabric.IText.prototype.controls.mr.cursorStyleHandler,
    withConnection: false,
    render: renderRectControl,
  });

  /*
    Currently only commented out incase UX wants to's to revert this change
  */
  // top side
  /* fabric.Object.prototype.controls.mt = new fabric.Control({
    x: 0,
    y: -0.5,
    offsetY: -4,
    actionHandler: fabric.Object.prototype.controls.mt.actionHandler,
    cursorStyleHandler: fabric.Object.prototype.controls.mt.cursorStyleHandler,
    withConnection: false,
    render: renderRectControl,
  });*/
  // bottom side
  /* fabric.Object.prototype.controls.mb = new fabric.Control({
    x: 0,
    y: 0.5,
    offsetY: 4,
    actionHandler: fabric.Object.prototype.controls.mb.actionHandler,
    cursorStyleHandler: fabric.Object.prototype.controls.mb.cursorStyleHandler,
    withConnection: false,
    render: renderRectControl,
  });*/
}

export function initializeCustomScalingControllers(): void {
  const cornerControlHandler = function (
    eventData: MouseEvent,
    transformData: fabric.Transform,
    x: number,
    y: number,
  ): boolean {
    const scale = getScale(eventData, transformData, x, y, {});
    const shouldDisableScaling = shouldDisableScalingDueToFontSizeLimit(
      transformData.target,
      (scale as any).scaleY,
    );
    if (shouldDisableScaling) {
      return true;
    }

    const isScalingApplied = (fabric as any).controlsUtils.scalingEqually(
      eventData,
      transformData,
      x,
      y,
    );
    handleUniformScalingForTextbox(transformData.target, isScalingApplied);
    applyScaleLimits(transformData.target);
    return isScalingApplied;
  };

  fabric.Object.prototype.controls.tr.actionHandler = cornerControlHandler;
  fabric.Object.prototype.controls.tl.actionHandler = cornerControlHandler;
  fabric.Object.prototype.controls.br.actionHandler = cornerControlHandler;
  fabric.Object.prototype.controls.bl.actionHandler = cornerControlHandler;

  const horizontalControlHandler = function (
    eventData: MouseEvent,
    transformData: fabric.Transform,
    x: number,
    y: number,
  ): boolean {
    const isScalingApplied = (fabric as any).controlsUtils.scalingXOrSkewingY(
      eventData,
      transformData,
      x,
      y,
    );
    handleHorizontalGroupScalingForTextbox(transformData.target, isScalingApplied);
    return isScalingApplied;
  };

  fabric.ActiveSelection.prototype.controls.mr.actionHandler = horizontalControlHandler;
  fabric.ActiveSelection.prototype.controls.ml.actionHandler = horizontalControlHandler;
}

/**
 * Set limits on the scale value of the obejct
 * We introduced this to fix an issue where the scale value becomes too large while resizing straight lines,
 * making them disappear from the canvas - related ticket: SPAC-13187
 */
const applyScaleLimits = function (target: fabric.Object) {
  if (target.scaleX) {
    target.scaleX = applyScaleLimit(target.scaleX);
  }
  if (target.scaleY) {
    target.scaleY = applyScaleLimit(target.scaleY);
  }
};

const applyScaleLimit = function (scale: number): number {
  const sign = Math.sign(scale);
  const absScale = Math.abs(scale);
  return sign * clamp(absScale, SCALE_LIMITS.MIN, SCALE_LIMITS.MAX);
};

// this function is used only when ENABLE_CUSTOM_TEXTBOX_BEHAVIOR flag is false
// otherwise, we handle scaling limits in initializeCustomScalingControllers
export function overrideCornerControlHandler(): void {
  const cornerControlHandler = function (
    eventData: MouseEvent,
    transformData: fabric.Transform,
    x: number,
    y: number,
  ): boolean {
    // call the original action handler for scaling from corner
    const isScalingApplied = (fabric as any).controlsUtils.scalingEqually(
      eventData,
      transformData,
      x,
      y,
    );
    applyScaleLimits(transformData.target);
    return isScalingApplied;
  };
  fabric.Object.prototype.controls.tr.actionHandler = cornerControlHandler;
  fabric.Object.prototype.controls.tl.actionHandler = cornerControlHandler;
  fabric.Object.prototype.controls.br.actionHandler = cornerControlHandler;
  fabric.Object.prototype.controls.bl.actionHandler = cornerControlHandler;
}

export function updateCanvasSize(
  size: { width: number; height: number },
  canvas: fabric.Canvas | fabric.StaticCanvas | undefined,
): void {
  applyCanvasSize(size, canvas);

  // Safari/iPadOS may report delayed sizes; apply double-pass updates
  modifiedSetTimeout(
    () => {
      applyCanvasSize(size, canvas);
    },
    100,
    true,
  );
}

export function applyCanvasSize(
  size: { width: number; height: number },
  canvas: fabric.Canvas | fabric.StaticCanvas | undefined,
): void {
  if (size.width <= 0 || size.height <= 0) {
    return;
  }
  canvas?.setDimensions({ width: size.width, height: size.height });
}

interface PencilCanvas {
  getCanvasSize(): [number, number];
  stateManager: BoardItemsStateManager;
}

interface HTMLCanvasContext {
  viewContainer: ViewContainerRef;
  htmlCanvas: ElementRef<HTMLDivElement>;
}

export class YCanvas extends fabric.Canvas implements PencilCanvas {
  public isStatic = false;
  public stateManager: BoardItemsStateManager;
  public boardId?: string;
  private subscribedToDisableSelectionForAddedObjects = false;
  isCanvasLockedForUser = false;
  canvasItemsInteractionsEnabled = false;
  private disableSelection?: (e: fabric.IEvent) => void;

  // Fabric event observables
  public readonly onMouseMove$: Observable<fabric.IEvent> = fromEvent(this, 'mouse:move');
  public readonly onMouseUp$: Observable<fabric.IEvent> = fromEvent(this, 'mouse:up');
  public readonly onMouseDown$: Observable<fabric.IEvent> = fromEvent(this, 'mouse:down');

  public htmlContext?: HTMLCanvasContext;

  /**
   * Set by Luna workspace mode to keep the camera on the whiteboard column. Every fabric camera
   * change (zoomToPoint, setZoom, absolutePan, relativePan) ends in setViewportTransform, so all
   * of them pass through it. Only this main canvas has it; the grid, overlay and thumbnail
   * canvases share the prototype patch above and must not.
   */
  public viewportConstraint?: (vpt: number[]) => number[];

  /** Set with `viewportConstraint`: the zoom a zoom gesture may ask for, applied before zooming. */
  public viewportZoomClamp?: (zoom: number) => number;

  override setViewportTransform(vpt: number[]): fabric.Canvas {
    return super.setViewportTransform(this.viewportConstraint ? this.viewportConstraint(vpt) : vpt);
  }

  constructor(
    element: HTMLCanvasElement | string | null,
    htmlContext?: HTMLCanvasContext,
    options?: fabric.ICanvasOptions,
  ) {
    super(element, options);
    this.stateManager = new BoardItemsStateManager(this);
    this.htmlContext = htmlContext;

    this.renderOnAddRemove = false;
    this.skipOffscreen = true;
    this.skipTargetFind = true; // improve mouse tracking performance when object selection is not needed
  }

  /**
   * the clear function is the function will run once we call fabricCanvas.clear()
   * it will set current objects for the canvas to be 0
   * also it will delete any background image or color
   * it will also clear the context for that canvas and rerender
   * here we override the function from Fabric v5 to prevent the object to be removed
   * as in Fabric v5 clear method they're calling
   * this.remove(_objects) which will cause our objected to be deleted from the Y.doc
   */
  clear() {
    this._objects.length = 0;
    this.backgroundImage = undefined;
    this.overlayImage = undefined;
    this.backgroundColor = '';
    this.overlayColor = '';
    if ((this as any)._hasITextHandlers) {
      this.off('mouse:up', (this as any)._mouseUpITextHandler);
      (this as any)._iTextInstances = null;
      (this as any)._hasITextHandlers = false;
    }
    if ((this as any).contextContainer) {
      this.clearContext((this as any).contextContainer);
    }
    this.renderOnAddRemove && this.requestRenderAll();
    return this;
  }

  /**
   * Clears the canvas like clear(), but first fires 'removed' on every object so board items with
   * HTML content (Angular componentRefs) dispose their DOM. The overridden clear() above drops
   * objects without going through remove()/'removed' (to avoid touching the Y.doc), which would
   * otherwise leave board-item overlays mounted and leaking into the next space on an in-place
   * switch. Firing 'removed' on the object (not 'object:removed' on the canvas) cleans up the HTML
   * without syncing a deletion.
   */
  clearWithObjectDomCleanup() {
    const objects = [...this._objects];
    this.clear();
    for (const object of objects) {
      object.fire('removed');
    }
    return this;
  }

  changeCanvasSelection(selectable: boolean, hoverCursor = 'default') {
    this.forEachObject((obj) => {
      this.changeSelection(obj, selectable, hoverCursor);
    });

    if (!this.disableSelection) {
      this.disableSelection = (e) => {
        this.changeSelection(e.target, false, hoverCursor);
      };
    }

    // Preventing subscribing multiple times,
    // and the imbalance between the number of call to subscribe and the ones to unsubscribe
    if (selectable && this.subscribedToDisableSelectionForAddedObjects) {
      this.off('object:added', this.disableSelection);
    } else if (!selectable && !this.subscribedToDisableSelectionForAddedObjects) {
      this.on('object:added', this.disableSelection);
      this.subscribedToDisableSelectionForAddedObjects = true;
    }
    this.canvasItemsInteractionsEnabled = selectable;
  }

  private changeSelection(obj: fabric.Object | undefined, selectable: boolean, hoverCursor): void {
    if (!obj) {
      return;
    }
    if (obj['itemId'] && !obj['customSelectable']) {
      obj.selectable = false;
    } else {
      obj.selectable = selectable;
    }

    obj.hoverCursor = hoverCursor;

    if (obj.selectable) {
      obj.hoverCursor = 'move';
    }
  }

  // this function checks if ctrl or shift keys are hold while clicking
  _isSelectionKeyPressed(e) {
    if (!this.selectionKey) {
      return false;
    }

    if (Array.isArray(this.selectionKey)) {
      return Boolean(
        this.selectionKey.find(function (key) {
          return e[key] === true;
        }),
      );
    }

    return Boolean(e[this.selectionKey]);
  }

  _shouldClearSelection(e, target?: fabric.Object): boolean {
    // the following code is our customizing to fix the weird behavior when user
    // hold ctrl key and db click an object, fabric draw controls even
    // event if hasControls is set to false
    if (
      (target?.hasControls === false ||
        !target?.controls ||
        (target?.controls && Object.keys(target?.controls).length === 0)) &&
      this._isSelectionKeyPressed(e)
    ) {
      return true;
    }

    // the following code is the default function code in fabric base
    const activeObjects = this.getActiveObjects();
    const activeObject = this._activeObject;

    return (
      !target ||
      (target &&
        activeObject &&
        activeObjects.length > 1 &&
        activeObjects.indexOf(target) === -1 &&
        activeObject !== target &&
        !this._isSelectionKeyPressed(e)) ||
      (target && !target.evented) ||
      (target && !target.selectable && activeObject && activeObject !== target)
    );
  }

  getCanvasSize(): [number, number] {
    return [this.getWidth(), this.getHeight()];
  }

  static isEmptyTextbox(textbox: fabric.IText): boolean {
    // return true if empty or contains only spaces and new lines
    const text = (textbox?.text ?? '').trim();
    return text === '';
  }

  public cleanEmptyTextbox(textbox: fabric.IText): boolean {
    let shouldDeleteTextbox = false;
    if (!YCanvas.isEmptyTextbox(textbox)) {
      return shouldDeleteTextbox;
    }
    // if the textbox is inside a sticky note, skip
    if ((textbox as any).parentBoardItem) {
      return shouldDeleteTextbox;
    }
    this.remove(textbox);
    shouldDeleteTextbox = true;
    if (this.getActiveObject() === textbox) {
      this.discardActiveObject();
    }
    this.requestRenderAll();
    return shouldDeleteTextbox;
  }
}

export class YStaticCanvas extends fabric.StaticCanvas implements PencilCanvas {
  public isStatic = true;
  public stateManager: BoardItemsStateManager;
  public htmlContext?: HTMLCanvasContext;
  public boardId?: string;

  constructor(
    element: HTMLCanvasElement | string | null,
    htmlContext?: HTMLCanvasContext,
    options?: fabric.ICanvasOptions,
  ) {
    super(element, options);
    this.renderOnAddRemove = false;
    this.htmlContext = htmlContext;
    this.stateManager = new BoardItemsStateManager(this);
  }

  getCanvasSize(): [number, number] {
    return [this.getWidth(), this.getHeight()];
  }
}

export const itemIsScribbleOrHighlight = (item: fabric.Object): boolean =>
  item.type === 'path' && !isBoardLine(item);

export function itemIsText(item: fabric.Object): boolean {
  return item.type === 'i-text' || item.type === 'textbox';
}

export function isShape(item: fabric.Object): boolean {
  const shapeTypes = new Set(['polygon', 'rect', 'ellipse', 'triangle']);
  if (!item.type) {
    return false;
  }
  return shapeTypes.has(item.type);
}

export const itemIsTextBox = (item: fabric.Object): item is fabric.Textbox =>
  item.type === 'textbox';
export const itemIsTextBoxOrItext = (item: fabric.Object): item is fabric.Textbox | fabric.IText =>
  item.type === 'i-text' || item.type === 'textbox';

export function setObjectCaching(cachingEnabled: boolean, includeScribbles: boolean) {
  fabric.Object.prototype.objectCaching = false;
  // set object caching for textboxes and sticky notes for desktop devices
  fabric.IText.prototype.objectCaching = cachingEnabled;
  fabric.Textbox.prototype.objectCaching = cachingEnabled;
  if (includeScribbles) {
    // set object caching for scribbles (fabric.Path)
    fabric.Path.prototype.objectCaching = cachingEnabled;
  }
}

function doRectanglesIntersectOnly(
  pointTL_A: fabric.Point,
  pointBR_A: fabric.Point,
  pointTL_B: fabric.Point,
  pointBR_B: fabric.Point,
): boolean {
  // Check if one rectangle is to the left of the other
  if (
    pointBR_A.x < pointTL_B.x ||
    pointBR_B.x < pointTL_A.x ||
    pointTL_A.y > pointBR_B.y ||
    pointTL_B.y > pointBR_A.y
  ) {
    return false;
  }

  // Check if rectangle A is completely inside rectangle B
  if (
    pointTL_A.x >= pointTL_B.x &&
    pointBR_A.x <= pointBR_B.x &&
    pointTL_A.y >= pointTL_B.y &&
    pointBR_A.y <= pointBR_B.y
  ) {
    return false;
  }

  // Check if rectangle B is completely inside rectangle A
  if (
    pointTL_B.x >= pointTL_A.x &&
    pointBR_B.x <= pointBR_A.x &&
    pointTL_B.y >= pointTL_A.y &&
    pointBR_B.y <= pointBR_A.y
  ) {
    return false;
  }

  return true;
}

function isRectangle(
  tl: fabric.Point,
  tr: fabric.Point,
  br: fabric.Point,
  bl: fabric.Point,
): boolean {
  return isOrthogonal(tl, tr, br) && isOrthogonal(tr, br, bl) && isOrthogonal(br, bl, tl);
}

function isOrthogonal(a: fabric.Point, b: fabric.Point, c: fabric.Point): boolean {
  return (b.x - a.x) * (b.x - c.x) + (b.y - a.y) * (b.y - c.y) === 0;
}

// TODO <Hatahet> split into two functions one for getting the absolute position and one for the cloning (the usage should clone then get the one with absolute position)
export function getFabricObjectWithAbsolutePosition(object: FabricObject) {
  if (!object.group) {
    return cloneFabricObject(object);
  }
  const objectTransformation = object.calcTransformMatrix();
  const objectState = fabric.util.qrDecompose(objectTransformation);
  const { flipX, flipY } = object;

  // To not affect the original object when setting the state to be absolute
  const absolutePositionedObject = cloneFabricObject(object);

  absolutePositionedObject.set(objectState);

  // Because in group selection, the origin of the transformation is the center
  absolutePositionedObject.setPositionByOrigin(
    new fabric.Point(objectState.translateX, objectState.translateY),
    'center',
    'center',
  );

  // This is here because the setting of flipX and flipY is absolute and not based on the previous state.
  absolutePositionedObject.flipX = absolutePositionedObject.flipX ? !flipX : flipX;
  absolutePositionedObject.flipY = absolutePositionedObject.flipY ? !flipY : flipY;

  return absolutePositionedObject;
}

// Temporary until all objects belong to the BoardItemComponent class, so we can implement a clone method there
export function cloneFabricObject(object: FabricObject): FabricObject {
  const clonedObject = fabric.util.object.clone(object);
  if (isBoardItem(object)) {
    clonedObject.toFrameItem = object.toFrameItem;
    clonedObject.getContentId = (object as any).getContentId;

    clonedObject.configurations = { ...object.configurations };
    Object.defineProperty(clonedObject, 'vuid', {
      get() {
        return clonedObject.configurations.vuid;
      },
      set(value) {
        clonedObject.configurations.vuid = value;
      },
    });
    Object.defineProperty(clonedObject, 'uid', {
      get() {
        return clonedObject.configurations.uid;
      },
      set(value) {
        clonedObject.configurations.uid = value;
      },
    });

    clonedObject.hasHtmlContent = object.hasHtmlContent;
    clonedObject.isStoredAsFrameItem = object.isStoredAsFrameItem;
    // preserve board-item specific transformation hook on clones
    clonedObject.handleInitializationFromPaste = object.handleInitializationFromPaste;
  }
  clonedObject.getPropertiesToInclude = (object as any).getPropertiesToInclude;
  clonedObject.toObject = (x) =>
    object.toObject.apply(clonedObject, [[...(x ?? []), ...customFabricFields]]);

  return clonedObject;
}

export function makeCanvasFitToContent(
  fabricCanvas: fabric.Canvas | fabric.StaticCanvas,
  boardItems?: fabric.Object[],
  canvasWidth?: number,
  canvasHeight?: number,
  minZoom = -Infinity,
  horizontalPadding = 0,
  verticalPadding = 0,
  // in case we want to consider the height a certain number rather than calculating it based on the board items bounding box
  customerBoardItemsHeight?: number,
) {
  boardItems = boardItems ?? fabricCanvas.getObjects();

  // Before creating a new group, we should remove the existing group (meaning discarding the selection)
  // to avoid issues when applying the transformation to the selected objects
  // Doing this only for non-static canvases
  if ('discardActiveObject' in fabricCanvas) {
    fabricCanvas.discardActiveObject();
  }

  // Getting content bounding box
  const group = new fabric.Group(boardItems);
  group.setCoords();
  const contentBoundingBox = group.getBoundingRect();
  group?.destroy();

  canvasWidth = canvasWidth ?? fabricCanvas.getWidth();
  canvasHeight = canvasHeight ?? fabricCanvas.getHeight();

  if (customerBoardItemsHeight !== undefined) {
    contentBoundingBox.height = customerBoardItemsHeight;
  }

  contentBoundingBox.height = Math.min(contentBoundingBox.height, MAX_FIT_TO_VIEW_CONTENT_HEIGHT);

  // Setting zoom value
  const contentWidth = contentBoundingBox.width + horizontalPadding;
  const contentHeight = contentBoundingBox.height + verticalPadding;
  const scaleX = canvasWidth / contentWidth;
  const scaleY = canvasHeight / contentHeight;
  const plainScale = Math.min(scaleX, scaleY);
  const scale = clamp(plainScale, minZoom / 100, MAX_ZOOM / 100);
  fabricCanvas.setZoom(scale);

  // Setting the center of the viewport to the center of the content
  const contentCenterX = contentBoundingBox.left + contentBoundingBox.width / 2;
  const contentCenterY = contentBoundingBox.top + contentBoundingBox.height / 2;
  const translateX = contentCenterX * scale - canvasWidth / 2;
  const translateY = contentCenterY * scale - canvasHeight / 2;
  fabricCanvas.absolutePan({ x: translateX, y: translateY });

  fabricCanvas?.requestRenderAll();
}

export const ensureAllObjectAreLive = (
  objects: FabricObject[],
  callback: (objects: FabricObject[]) => void,
) => {
  const plainFabricObjects = objects.filter((o) => !isBoardItem(o));

  if (!plainFabricObjects) {
    // all objects are live, return them
    callback(objects);
    return;
  }

  // Add plain fabric objects there is a need to enliven
  fabric.util.enlivenObjects(
    plainFabricObjects,
    (enlivenedObjects) => {
      // replacing each dormant object with the live one, pertaining the same array order

      const uidToEnlivenedObject = new Map<string, FabricObject>();
      for (const enlivenedObject of enlivenedObjects) {
        uidToEnlivenedObject.set(enlivenedObject.uid, enlivenedObject);
      }

      for (let i = 0; i < objects.length; i++) {
        const objectUid = objects[i].uid;
        if (!objectUid) {
          continue;
        }
        const enlivenedObject = uidToEnlivenedObject.get(objectUid);
        if (enlivenedObject) {
          objects[i] = enlivenedObject;
        }
      }
      callback(objects);
    },
    '',
  );
};

// Store original functions to prevent multiple overrides
let originalPolygonInitialize: Function | null = null;
let originalTextboxInitialize: Function | null = null;

function overridePolygoneInitialization(): void {
  // Only override if we haven't already done so
  if (originalPolygonInitialize === null) {
    originalPolygonInitialize = fabric.Polygon.prototype.initialize;
    fabric.Polygon.prototype.initialize = function (...args) {
      const result = originalPolygonInitialize!.apply(this, args);
      if (isOldArrowLine(this)) {
        // make sure the fill value matches the stroke for arrows
        this.fill = this.stroke;
      }
      return result;
    };
  }
}

function overrideTextboxInitialization(): void {
  // Only override if we haven't already done so
  if (originalTextboxInitialize === null) {
    originalTextboxInitialize = fabric.Textbox.prototype.initialize;
    fabric.Textbox.prototype.initialize = function (...args) {
      const result = originalTextboxInitialize!.apply(this, args);
      // clean styles on initialization to remove any redundant styles
      cleanAllStyles(this as fabric.Textbox);
      if ((this as any).hasMixedSizes) {
        updateMixedFontSizesState(this as fabric.Textbox);
      }
      return result;
    };
  }
}

// old implementation of arrow line
// check if the object is an arrow line
// as we don't have a type for it, we're using the points length
// currently we only have 7 points for an arrow line
export const isOldArrowLine = (object: fabric.Object): boolean =>
  (object as any)?.points?.length === 7;

export function isLineOrArrowShape(object: fabric.Object): boolean {
  // check for new and old line/arrow shapes
  return (
    (object as any).itemType === ItemModel.Line ||
    object.type === 'line' ||
    (object.type === 'polygon' && (object as any)?.points?.length === 7)
  );
}
export const isOldStickyNote = (object: fabric.Object): boolean =>
  ATTR_FIELD_NAME in object && Boolean(object[ATTR_FIELD_NAME]);

export const CUSTOM_FABRIC_DATA = 'custom-fabric-data';
export const ATTR_FIELD_NAME = 'customObjectType';
